import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useEmailContext } from '../../../contexts/EmailContext';
import { extractEmailData, getCustomerDisplayName } from '../../../utils/emailContactHelpers';
import { EmailMessage } from '../../../models/Email';
import { fetchEmailThread, ThreadContact } from '../../../services/fetchEmailThread';
import { fetchEmailContent, EmailContentTransfer } from '../../../services/fetchEmailContent';
import { EMAIL_INITIATION_METHODS } from '../../../constants/email';
import { useEnrichEmailWithContent } from './useEnrichEmailWithContent';

export function findTransferContactIds(contacts: ThreadContact[], activeContactId: string): string[] {
  const byId = new Map(contacts.map(c => [c.contactId, c]));
  let current = byId.get(activeContactId);
  const transferContactIds: string[] = [];

  while (current) {
    if (current.initiationMethod.toUpperCase() === EMAIL_INITIATION_METHODS.TRANSFER) {
      transferContactIds.push(current.contactId);
    }
    const previousId = current.previousContactId ?? current.relatedContactId;
    current = previousId ? byId.get(previousId) : undefined;
  }

  return transferContactIds;
}

export function filterThreadContacts(
  contacts: ThreadContact[],
  activeContactId: string
): { filtered: ThreadContact[]; wasTransferred: boolean; transferContactIds: string[] } {
  const previousContactIdsSet = new Set(contacts.map(c => c.previousContactId).filter(Boolean));
  const filtered = contacts.filter(c => c.contactId === activeContactId || !previousContactIdsSet.has(c.contactId));
  const transferContactIds = findTransferContactIds(contacts, activeContactId);
  return { filtered, wasTransferred: transferContactIds.length > 0, transferContactIds };
}

interface UseEmailContactHandlersProps {
  onError?: (message: string) => void;
}

export function useEmailContactHandlers({ onError }: UseEmailContactHandlersProps): void {
  const { updateEmailData, updateEmailThread, startLoadingThread, stopLoadingThread } = useEmailContext();
  const enrichEmailWithContent = useEnrichEmailWithContent();
  const isInitializedRef = useRef(false);
  const onErrorRef = useRef(onError);
  const enrichEmailWithContentRef = useRef(enrichEmailWithContent);

  onErrorRef.current = onError;
  enrichEmailWithContentRef.current = enrichEmailWithContent;

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const handleExtractEmailData = (contact: connect.Contact): void => {
      updateEmailData(contact.getContactId(), extractEmailData(contact));
    };

    const handleFetchEmailThread = async (contact: connect.Contact): Promise<void> => {
      const activeContactId = contact.getContactId();
      const contactAssociationId = contact.getContactAssociationId();
      if (!contactAssociationId) return;

      try {
        startLoadingThread(activeContactId);

        const [contacts, activeContent] = await Promise.all([
          fetchEmailThread(contactAssociationId),
          fetchEmailContent(activeContactId).catch(error => {
            console.error('Failed to fetch active email content:', error);
            onErrorRef.current?.('Failed to load the email content. Please try again.');
            return undefined;
          })
        ]);

        const { filtered: filteredContacts, transferContactIds } = filterThreadContacts(contacts, activeContactId);
        const customerDisplayName = getCustomerDisplayName(contact);

        const transferEntries = await Promise.all(
          transferContactIds.map(async contactId => {
            const details =
              contactId === activeContactId
                ? (activeContent?.transferInfo ?? undefined)
                : ((await fetchEmailContent(contactId).catch(() => null))?.transferInfo ?? undefined);
            return details ? ([contactId, details] as const) : undefined;
          })
        );
        const transfersByContactId = new Map(
          transferEntries.filter((entry): entry is [string, EmailContentTransfer] => !!entry)
        );

        const messages: EmailMessage[] = filteredContacts
          .map(
            ({ contactId, initiationTimestamp }): EmailMessage => ({
              contactId,
              to: [],
              cc: [],
              timestamp: new Date(initiationTimestamp).getTime()
            })
          )
          .map(message =>
            message.contactId === activeContactId && activeContent
              ? enrichEmailWithContentRef.current(message, activeContent)
              : message
          )
          .map(message =>
            message.contactId === activeContactId && message.from && customerDisplayName
              ? { ...message, from: { ...message.from, displayName: customerDisplayName } }
              : message
          )
          .map(message => {
            const transferInfo = transfersByContactId.get(message.contactId);
            return transferInfo ? { ...message, transferInfo } : message;
          })
          .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

        updateEmailThread(activeContactId, { contactAssociationId, messages });
      } catch (error) {
        console.error('Failed to fetch email thread:', error);
        onErrorRef.current?.('Failed to load the email thread. Please try again.');
      } finally {
        stopLoadingThread(activeContactId);
      }
    };

    const handleEmailContact = (contact: connect.Contact): void => {
      handleExtractEmailData(contact);

      contact.onConnected(() => {
        handleExtractEmailData(contact);
        void handleFetchEmailThread(contact);
      });
    };

    connect.agent(agent => {
      agent
        .getContacts()
        .filter(c => (c.getType() as string) === connect.ContactType.EMAIL)
        .filter(c => c.getInitiationMethod()?.toUpperCase() !== EMAIL_INITIATION_METHODS.AGENT_REPLY)
        .forEach(handleEmailContact);
    });

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      const initiationMethod = contact.getInitiationMethod() as string;

      if (initiationMethod?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
        return;
      }

      handleEmailContact(contact);
    });
  }, [startLoadingThread, stopLoadingThread, updateEmailData, updateEmailThread]);
}
